import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, 
  Send, 
  X, 
  Sparkles, 
  Brain, 
  Globe, 
  Bot, 
  Maximize2, 
  Minimize2, 
  Trash2, 
  ArrowRight,
  Paperclip,
  Upload,
  File,
  FileText,
  CheckCircle2,
  DollarSign,
  Clock,
  Database,
  ExternalLink,
  MapPin,
  Building
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { db, setDoc, doc, serverTimestamp } from '../firebase';
import { getSafeSolicitationDocId, ensureFirestoreSafeSize } from '../utils/firestoreSanitizer';

interface AttachedChatFile {
  name: string;
  size: number;
  type: string;
  base64?: string;
  text?: string;
}

interface CreatedRFXData {
  solicitationId: string;
  title: string;
  state: string;
  agency: string;
  description?: string;
  dueDate?: string;
  category?: string;
  pricing?: number;
  sourceUrl?: string;
  rows?: any[];
  documents?: any[];
}

interface ChatMessage {
  role: 'user' | 'model';
  parts: { text?: string; inlineData?: { mimeType: string; data: string } }[];
  timestamp: string;
  isFallback?: boolean;
  groundingSources?: { title: string; uri: string }[];
  attachedFiles?: AttachedChatFile[];
  createdRfx?: CreatedRFXData;
}

export const AIPortalsChatbot: React.FC<{ showAiUi?: (type: string) => void }> = ({ showAiUi }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<AttachedChatFile[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  
  // Model & Mode parameters
  const [selectedModel, setSelectedModel] = useState<'gemini-3.7-flash' | 'gemini-3.1-pro-preview' | 'gemini-3.1-flash-lite'>('gemini-3.7-flash');
  const [useSearch, setUseSearch] = useState(true);
  const [highThinking, setHighThinking] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load chat history from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('rfxcelerate_chat_history');
    if (saved) {
      try {
        setMessages(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse chat history:', e);
      }
    } else {
      // Friendly greeting message from RFXcelerate AI
      setMessages([
        {
          role: 'model',
          parts: [{ text: "Hello! I am **RFXcelerate AI**, your specialized procurement advisor. I can help you research government solicitations, analyze uploaded RFP documents, draft compliant bid proposals, and **create new RFX opportunities in your database upon request**!\n\nHow can I assist you today?" }],
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ]);
    }
  }, []);

  // Save chat history on change (sanitized without heavy base64 strings to prevent localStorage QuotaExceeded errors)
  const saveHistory = (newMsgs: ChatMessage[]) => {
    setMessages(newMsgs);
    try {
      // Keep only the last 30 messages and strip raw base64 data payloads
      const sanitized = newMsgs.slice(-30).map(m => ({
        role: m.role,
        parts: (m.parts || [])
          .filter(p => p.text)
          .map(p => ({ text: p.text })),
        timestamp: m.timestamp,
        isFallback: m.isFallback,
        groundingSources: m.groundingSources,
        attachedFiles: m.attachedFiles?.map(f => ({
          name: f.name,
          size: f.size,
          type: f.type
          // Base64 explicitly omitted from persistent storage
        })),
        createdRfx: m.createdRfx
      }));

      localStorage.setItem('rfxcelerate_chat_history', JSON.stringify(sanitized));
    } catch (err) {
      console.warn('localStorage quota exceeded while saving chat history. Truncating history...', err);
      try {
        // Fallback: save only the last 5 messages without attachments
        const minimalist = newMsgs.slice(-5).map(m => ({
          role: m.role,
          parts: (m.parts || []).filter(p => p.text).map(p => ({ text: p.text })),
          timestamp: m.timestamp
        }));
        localStorage.setItem('rfxcelerate_chat_history', JSON.stringify(minimalist));
      } catch {
        // Safe degrade if browser storage is completely full
      }
    }
  };

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // File upload processing helper
  const processFiles = (files: FileList | File[]) => {
    const filesArray = Array.from(files);
    filesArray.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Extract base64 without prefix
        const base64Data = result.includes(',') ? result.split(',')[1] : result;
        
        const newFile: AttachedChatFile = {
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          base64: base64Data
        };

        setAttachedFiles(prev => [...prev, newFile]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  };

  // Persist created RFX to Firestore
  const persistCreatedRFX = async (rfxData: CreatedRFXData) => {
    try {
      const docId = getSafeSolicitationDocId(rfxData.solicitationId || `RFX-${Date.now()}`);
      const priceNum = typeof rfxData.pricing === 'number' 
        ? rfxData.pricing 
        : (parseFloat(String(rfxData.pricing || '150000').replace(/[^0-9.]/g, '')) || 150000);

      const fullOpportunity = {
        solicitationId: docId,
        title: rfxData.title || 'Untitled RFX Solicitation',
        agency: rfxData.agency || `${rfxData.state || 'TX'} State Procurement Bureau`,
        state: (rfxData.state || 'TX').toUpperCase(),
        category: rfxData.category || 'Information Technology',
        pricing: priceNum,
        dueDate: rfxData.dueDate ? new Date(rfxData.dueDate).toISOString() : new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString(),
        status: 'open',
        description: rfxData.description || 'Custom RFX Opportunity created by RFXcelerate AI Assistant.',
        sourceUrl: rfxData.sourceUrl || `https://procure.${(rfxData.state || 'tx').toLowerCase()}.gov/opportunity/${docId.toLowerCase()}`,
        importSource: 'RFXcelerate AI Assistant',
        details: {
          generalinformation: {
            solicitationNumber: docId,
            estimatedBudget: `$${priceNum.toLocaleString()}`,
            buyingAgency: rfxData.agency || `${rfxData.state || 'TX'} State Procurement Bureau`,
            status: 'Active/Open'
          },
          response: {
            rows: rfxData.rows && rfxData.rows.length > 0
              ? rfxData.rows.map((r: any, idx: number) => ({
                  '0': String(idx + 1),
                  '1': r.item || 'Service Delivery Scope',
                  '2': r.line || `Line-00${idx + 1}`,
                  '4': 'Service',
                  '5': r.item || 'Deliverable Item',
                  '10': String(r.qty || 1),
                  '11': r.unit || 'Lot'
                }))
              : [
                  { '0': '1', '1': `${rfxData.title} - Scope Execution`, '2': 'IT-001', '4': 'Service', '5': 'Core Implementation', '10': '1', '11': 'Lot' },
                  { '0': '2', '1': 'Quality Assurance & Handover', '2': 'IT-002', '4': 'Service', '5': 'Handover Support', '10': '1', '11': 'Lot' }
                ]
          },
          solicitationdocuments: rfxData.documents && rfxData.documents.length > 0
            ? rfxData.documents.map((docItem: any) => ({
                name: docItem.name || 'Solicitation-Document.pdf',
                url: (docItem.url && docItem.url !== '#' && !docItem.url.startsWith('data:') && docItem.url.length < 500) 
                  ? docItem.url 
                  : `https://storage.procureflow.cloud/docs/${docId}/${encodeURIComponent(docItem.name || 'Requirements.pdf')}`,
                version: docItem.version || 1.0
              }))
            : [
                { name: `${docId}-Requirements.pdf`, url: `https://storage.procureflow.cloud/docs/${docId}/Requirements.pdf`, version: 1.0 }
              ]
        },
        createdAt: serverTimestamp()
      };

      const safePayload = ensureFirestoreSafeSize(fullOpportunity);
      await setDoc(doc(db, 'solicitations', docId), safePayload);
      
      // Dispatch database event
      window.dispatchEvent(new CustomEvent('rfx-database-updated', { detail: fullOpportunity }));
    } catch (err) {
      console.error('Error saving AI created RFX to Firestore:', err);
    }
  };

  const handleSendMessage = async (textToSend?: string) => {
    const messageContent = (textToSend || input).trim();
    if ((!messageContent && attachedFiles.length === 0) || loading) return;

    if (!textToSend) {
      setInput('');
    }

    const currentAttachments = [...attachedFiles];
    setAttachedFiles([]);

    // Construct parts array supporting text and multiple inline data attachments
    const messageParts: { text?: string; inlineData?: { mimeType: string; data: string } }[] = [];
    
    if (messageContent) {
      messageParts.push({ text: messageContent });
    }

    currentAttachments.forEach(file => {
      if (file.base64) {
        messageParts.push({
          inlineData: {
            mimeType: file.type,
            data: file.base64
          }
        });
      }
    });

    const newMsg: ChatMessage = {
      role: 'user',
      parts: messageParts,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      attachedFiles: currentAttachments.length > 0 ? currentAttachments : undefined
    };

    const updatedMessages = [...messages, newMsg];
    saveHistory(updatedMessages);
    setLoading(true);

    try {
      // Format messages in structured multi-turn format
      const apiMessages = updatedMessages.map(msg => ({
        role: msg.role,
        parts: msg.parts
      }));

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: apiMessages,
          model: selectedModel,
          thinkingLevel: highThinking ? 'HIGH' : undefined,
          useSearch: useSearch
        })
      });

      if (!response.ok) {
        throw new Error('API server returned an error');
      }

      const data = await response.json();
      
      // Extract grounding sources if search grounding was enabled
      let sources: { title: string; uri: string }[] = [];
      if (data.groundingMetadata?.groundingChunks) {
        sources = data.groundingMetadata.groundingChunks
          .map((chunk: any) => ({
            title: chunk.web?.title || 'Web Source',
            uri: chunk.web?.uri || ''
          }))
          .filter((item: any) => item.uri);
      }

      let createdRfxData: CreatedRFXData | undefined = data.createdRfx;

      // Also parse client-side if model returned rfx block
      if (!createdRfxData && data.text) {
        const match = data.text.match(/```(?:json:rfx_create|json)\s*([\s\S]*?)\s*```/i);
        if (match && match[1]) {
          try {
            const parsed = JSON.parse(match[1]);
            if (parsed.solicitationId || parsed.title) {
              createdRfxData = parsed;
            }
          } catch (e) {
            // Ignored
          }
        }
      }

      // If an RFX item was created, persist it directly to Firestore!
      if (createdRfxData) {
        await persistCreatedRFX(createdRfxData);
      }

      // Filter out raw JSON block from displayed markdown text for pristine readability
      let cleanText = data.text || 'I have processed your request.';
      cleanText = cleanText.replace(/```(?:json:rfx_create|json)\s*[\s\S]*?```/gi, '').trim();

      const botMsg: ChatMessage = {
        role: 'model',
        parts: [{ text: cleanText }],
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isFallback: data.isFallback,
        groundingSources: sources.length > 0 ? sources : undefined,
        createdRfx: createdRfxData
      };

      saveHistory([...updatedMessages, botMsg]);
    } catch (err) {
      console.error('Chat error:', err);
      const errorMsg: ChatMessage = {
        role: 'model',
        parts: [{ text: '⚠️ **System Outage / Connection Error**\n\nI was unable to reach the real-time AI engine. Please ensure your `GEMINI_API_KEY` is fully configured in **Settings > Secrets** or try again in a moment.' }],
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isFallback: true
      };
      saveHistory([...updatedMessages, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearHistory = () => {
    if (window.confirm("Are you sure you want to clear your conversation history?")) {
      const defaultMsg: ChatMessage[] = [
        {
          role: 'model',
          parts: [{ text: "Hello! I am **RFXcelerate AI**, your specialized procurement advisor. I can help you research government solicitations, analyze uploaded RFP documents, draft compliant bid proposals, and **create new RFX opportunities in your database upon request**!\n\nHow can I assist you today?" }],
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
      ];
      saveHistory(defaultMsg);
    }
  };

  const handleToggleHighThinking = (checked: boolean) => {
    setHighThinking(checked);
    if (checked) {
      setSelectedModel('gemini-3.1-pro-preview');
      if (showAiUi) {
        showAiUi('paid_model_flow');
      }
    } else {
      setSelectedModel('gemini-3.7-flash');
    }
  };

  const handleModelChange = (model: 'gemini-3.7-flash' | 'gemini-3.1-pro-preview' | 'gemini-3.1-flash-lite') => {
    setSelectedModel(model);
    if (model === 'gemini-3.1-pro-preview') {
      setHighThinking(true);
      if (showAiUi) {
        showAiUi('paid_model_flow');
      }
    } else {
      setHighThinking(false);
    }
  };

  const navigateToDatabase = () => {
    window.dispatchEvent(new CustomEvent('switch-tab', { detail: 'database' }));
  };

  const quickActions = [
    { label: "➕ Create $350k Texas Cloud RFP", query: "Please create an RFX item in the database for a $350,000 Texas Cloud Security RFP due in 45 days." },
    { label: "➕ Add California IT Opportunity", query: "Create an RFX item in the database for California Department of Technology titled 'Enterprise AI Sourcing & Automation' with budget $500,000." },
    { label: "Draft Executive Summary", query: "Can you draft a high-scoring Executive Summary for an IT modernization state proposal?" },
    { label: "Small Business / HUB Rules", query: "Explain how small business preference programs and HUB sub-contracting targets work in state contracting." }
  ];

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 p-4 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-xl transition-all duration-300 hover:scale-110 flex items-center gap-2 z-50 group border border-indigo-500/30"
          id="btn-ai-chat-toggle"
          title="Open Procurement Assistant"
        >
          <Sparkles className="w-5 h-5 text-amber-300 animate-pulse" />
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 ease-out font-medium text-sm whitespace-nowrap">
            AI Procurement Advisor
          </span>
          <MessageSquare className="w-5 h-5" />
        </button>
      )}

      {/* Floating Chat Container */}
      {isOpen && (
        <div 
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`fixed right-6 bottom-6 bg-white rounded-2xl shadow-2xl border ${
            isDraggingOver ? 'border-indigo-500 ring-4 ring-indigo-100' : 'border-gray-200'
          } overflow-hidden transition-all duration-300 flex flex-col z-50 ${
            isMinimized 
              ? 'w-80 h-14' 
              : 'w-96 md:w-[480px] h-[640px] max-h-[88vh]'
          }`}
          id="ai-assistant-container"
        >
          {/* Header */}
          <div className="bg-slate-900 text-white p-4 flex items-center justify-between border-b border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-indigo-600/30 text-indigo-400">
                <Bot className="w-5 h-5 text-indigo-400" />
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-tight flex items-center gap-1.5">
                  RFXcelerate AI Assistant
                  <span className="flex h-2 w-2 relative">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                </h3>
                <span className="text-[10px] text-slate-400 font-mono">
                  {selectedModel === 'gemini-3.1-pro-preview' ? '⚡ Gemini Pro (Thinking)' : '✨ Gemini Flash'} • Multi-file & RFX DB Ready
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button 
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 hover:bg-slate-800 rounded transition-colors text-slate-400 hover:text-white cursor-pointer"
                title={isMinimized ? "Expand" : "Minimize"}
              >
                {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
              </button>
              <button 
                onClick={handleClearHistory}
                className="p-1 hover:bg-slate-800 rounded transition-colors text-slate-400 hover:text-rose-400 cursor-pointer"
                title="Clear History"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-slate-800 rounded transition-colors text-slate-400 hover:text-white cursor-pointer"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Body and Input - Only visible when expanded */}
          {!isMinimized && (
            <>
              {/* Controls Header Panel */}
              <div className="bg-slate-50 border-b border-gray-200 px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-xs">
                {/* Model Selector */}
                <div className="flex items-center gap-1">
                  <span className="text-gray-500 font-medium">Model:</span>
                  <select 
                    value={selectedModel}
                    onChange={(e) => handleModelChange(e.target.value as any)}
                    className="bg-white border border-gray-200 rounded px-1.5 py-0.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 font-medium cursor-pointer"
                  >
                    <option value="gemini-3.7-flash">Gemini 3.7 Flash</option>
                    <option value="gemini-3.1-pro-preview">Gemini Pro (Advanced)</option>
                    <option value="gemini-3.1-flash-lite">Flash Lite (Fast)</option>
                  </select>
                </div>

                {/* Grounding & Thinking Toggles */}
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-1 cursor-pointer select-none font-medium text-gray-600 hover:text-gray-900">
                    <input 
                      type="checkbox" 
                      checked={useSearch}
                      onChange={(e) => setUseSearch(e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3 cursor-pointer"
                    />
                    <Globe className={`w-3.5 h-3.5 ${useSearch ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <span>Search</span>
                  </label>

                  <label className="flex items-center gap-1 cursor-pointer select-none font-medium text-gray-600 hover:text-gray-900">
                    <input 
                      type="checkbox" 
                      checked={highThinking}
                      onChange={(e) => handleToggleHighThinking(e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 w-3 h-3 cursor-pointer"
                    />
                    <Brain className={`w-3.5 h-3.5 ${highThinking ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <span className="flex items-center gap-0.5">
                      Thinking
                      {highThinking && <span className="text-[9px] px-1 bg-indigo-100 text-indigo-700 rounded font-bold uppercase tracking-wider animate-pulse">High</span>}
                    </span>
                  </label>
                </div>
              </div>

              {/* Drag overlay notice if dragging files over */}
              {isDraggingOver && (
                <div className="absolute inset-x-0 top-14 bottom-24 bg-indigo-900/80 backdrop-blur-xs z-30 flex flex-col items-center justify-center text-white p-6 text-center">
                  <Upload className="w-12 h-12 text-indigo-200 animate-bounce mb-2" />
                  <p className="font-bold text-base">Drop Solicitation Files Here</p>
                  <p className="text-xs text-indigo-200 mt-1">Upload multiple PDFs, spreadsheets, or specs for AI analysis and RFX database creation</p>
                </div>
              )}

              {/* Message Thread */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50/50">
                {messages.map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}
                  >
                    <div className="flex items-center gap-1 text-[10px] text-gray-400 mb-1 px-1">
                      <span>{msg.role === 'user' ? 'You' : 'RFXcelerate Advisor'}</span>
                      <span>•</span>
                      <span>{msg.timestamp}</span>
                    </div>

                    <div 
                      className={`max-w-[90%] rounded-2xl px-4 py-3 shadow-sm text-sm ${
                        msg.role === 'user' 
                          ? 'bg-indigo-600 text-white rounded-tr-none' 
                          : 'bg-white border border-gray-250 text-gray-800 rounded-tl-none'
                      }`}
                    >
                      {/* User message attached files badges */}
                      {msg.attachedFiles && msg.attachedFiles.length > 0 && (
                        <div className="mb-2 pb-2 border-b border-indigo-400/30 flex flex-wrap gap-1.5">
                          {msg.attachedFiles.map((file, fIdx) => (
                            <div 
                              key={fIdx} 
                              className="flex items-center gap-1.5 bg-indigo-700/60 text-white px-2 py-1 rounded-md text-[11px]"
                            >
                              <Paperclip className="w-3 h-3 text-indigo-200 shrink-0" />
                              <span className="font-medium truncate max-w-[140px]">{file.name}</span>
                              <span className="text-[9px] text-indigo-300 font-mono">{(file.size / 1024).toFixed(0)}KB</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Main Message Text */}
                      {msg.parts[0]?.text && (
                        <div className="prose prose-sm max-w-none break-words">
                          <ReactMarkdown>{msg.parts[0].text}</ReactMarkdown>
                        </div>
                      )}

                      {/* Interactive Created RFX Database Card */}
                      {msg.createdRfx && (
                        <div className="mt-3 p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl text-xs space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 font-bold text-emerald-800 text-[11px]">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              Saved to RFX Procurement Database
                            </span>
                            <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-mono font-bold rounded text-[10px]">
                              {msg.createdRfx.solicitationId}
                            </span>
                          </div>

                          <div className="font-bold text-gray-900 text-xs">
                            {msg.createdRfx.title}
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-[10.5px] text-gray-600 bg-white/70 p-2 rounded-lg border border-emerald-100">
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-indigo-500 shrink-0" />
                              <span className="font-medium">State:</span> {msg.createdRfx.state || 'TX'}
                            </div>
                            <div className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3 text-emerald-600 shrink-0" />
                              <span className="font-medium">Budget:</span> ${typeof msg.createdRfx.pricing === 'number' ? msg.createdRfx.pricing.toLocaleString() : msg.createdRfx.pricing}
                            </div>
                            <div className="flex items-center gap-1 col-span-2 truncate">
                              <Building className="w-3 h-3 text-slate-500 shrink-0" />
                              <span className="font-medium">Agency:</span> <span className="truncate">{msg.createdRfx.agency}</span>
                            </div>
                            {msg.createdRfx.dueDate && (
                              <div className="flex items-center gap-1 col-span-2">
                                <Clock className="w-3 h-3 text-amber-500 shrink-0" />
                                <span className="font-medium">Due Date:</span> {new Date(msg.createdRfx.dueDate).toLocaleDateString()}
                              </div>
                            )}
                          </div>

                          <button
                            onClick={navigateToDatabase}
                            className="w-full mt-1.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-[11px] flex items-center justify-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
                          >
                            <Database className="w-3.5 h-3.5" />
                            Open in RFX Database View
                          </button>
                        </div>
                      )}

                      {/* Fallback Badge */}
                      {msg.isFallback && msg.role === 'model' && (
                        <div className="mt-2 pt-1 border-t border-yellow-100 flex items-center gap-1 text-[10px] text-yellow-700 font-medium">
                          <span className="inline-block w-1.5 h-1.5 bg-yellow-400 rounded-full animate-ping"></span>
                          Offline / Intelligent Fallback Engine Active
                        </div>
                      )}

                      {/* Grounding Sources */}
                      {msg.groundingSources && msg.groundingSources.length > 0 && (
                        <div className="mt-2 pt-1.5 border-t border-gray-150">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Grounding Sources:</span>
                          <div className="flex flex-col gap-1 max-h-20 overflow-y-auto">
                            {msg.groundingSources.slice(0, 3).map((src, sIdx) => (
                              <a 
                                key={sIdx}
                                href={src.uri} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-[10px] text-indigo-600 hover:underline flex items-center gap-1 font-medium"
                              >
                                <Globe className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                                <span className="truncate">{src.title || src.uri}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Thinking / Loading Spinner */}
                {loading && (
                  <div className="flex flex-col items-start">
                    <div className="flex items-center gap-1 text-[10px] text-gray-400 mb-1 px-1">
                      <span>RFXcelerate Advisor</span>
                      <span>•</span>
                      <span className="animate-pulse">Analyzing & Synthesizing...</span>
                    </div>
                    <div className="bg-white border border-gray-250 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm flex items-center gap-2">
                      <div className="flex space-x-1.5">
                        <span className="h-2 w-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="h-2 w-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="h-2 w-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span className="text-xs text-indigo-600 font-semibold animate-pulse ml-1">
                        {highThinking ? 'Performing deep multi-criteria evaluation...' : 'Processing procurement instructions...'}
                      </span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Suggestions Panel (only shown when conversation is empty or has only greeting) */}
              {messages.length <= 1 && (
                <div className="p-3 bg-indigo-50/50 border-t border-gray-150">
                  <span className="text-[10px] font-bold text-indigo-500 uppercase tracking-wider block mb-1.5 px-1">Suggested Inquiries & Database Actions</span>
                  <div className="flex flex-col gap-1.5">
                    {quickActions.map((act, actIdx) => (
                      <button 
                        key={actIdx}
                        onClick={() => handleSendMessage(act.query)}
                        className="text-left bg-white hover:bg-indigo-50 border border-gray-150 hover:border-indigo-300 rounded-xl px-3 py-1.5 text-xs text-gray-700 font-medium transition-all flex items-center justify-between group cursor-pointer"
                      >
                        <span className="truncate">{act.label}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Active Attached Files Tray */}
              {attachedFiles.length > 0 && (
                <div className="px-3 py-2 bg-indigo-50/70 border-t border-indigo-100 flex flex-wrap items-center gap-1.5 max-h-24 overflow-y-auto">
                  <span className="text-[10px] font-bold text-indigo-700 uppercase mr-1 flex items-center gap-1">
                    <Paperclip className="w-3 h-3 text-indigo-600" />
                    Attached ({attachedFiles.length}):
                  </span>
                  {attachedFiles.map((file, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-1.5 bg-white border border-indigo-200 text-gray-800 px-2 py-1 rounded-lg text-xs shadow-2xs"
                    >
                      <File className="w-3.5 h-3.5 text-indigo-500" />
                      <span className="font-semibold text-[11px] truncate max-w-[130px]">{file.name}</span>
                      <span className="text-[9px] text-gray-400 font-mono">{(file.size / 1024).toFixed(0)}KB</span>
                      <button 
                        type="button"
                        onClick={() => handleRemoveFile(idx)}
                        className="text-gray-400 hover:text-rose-500 p-0.5 rounded cursor-pointer"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Footer Input Form */}
              <form 
                onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
                className="p-3 bg-white border-t border-gray-200 flex items-center gap-2"
              >
                {/* Hidden Multi-file input */}
                <input 
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  multiple
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json,image/*"
                />

                {/* Paperclip attachment button */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors cursor-pointer shrink-0 border border-gray-200"
                  title="Upload solicitation documents or specs (Multi-file)"
                >
                  <Paperclip className="w-4 h-4" />
                </button>

                <div className="flex-1 relative">
                  <textarea 
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder={attachedFiles.length > 0 ? "Add instructions for these files or request RFX creation..." : "Ask RFXcelerate or type 'Create RFX item for...'"}
                    rows={1}
                    className="w-full pl-3 pr-10 py-2 border border-gray-200 focus:border-indigo-500 rounded-xl text-xs focus:ring-1 focus:ring-indigo-500 outline-none resize-none font-sans"
                    style={{ maxHeight: '100px' }}
                  />
                  <div className="absolute right-2.5 bottom-2.5 text-[9px] text-gray-400 pointer-events-none hidden md:block">
                    Enter
                  </div>
                </div>

                <button 
                  type="submit"
                  disabled={(!input.trim() && attachedFiles.length === 0) || loading}
                  className="p-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-100 disabled:opacity-50 text-white rounded-xl transition-all cursor-pointer shadow-sm flex-shrink-0"
                  title="Send Message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};
